import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
    FormGroup,
    FormsModule,
    NgForm,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { CopyrightComponent } from 'app/components/copyright/copyright.component';
import { StartupLogoComponent } from 'app/components/startup-logo/startup-logo.component';
import { AuthService } from 'app/core/auth/auth.service';
import { Firebase2FAService } from 'app/core/auth/firebase/firebase-2fa.service';
import { FirebaseService } from 'app/core/auth/firebase/firebase.service';
import { User } from 'app/core/interfaces/user';
import { AlertService } from 'app/services/alert.service';
import { take } from 'rxjs';

const MatModules = [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
];

@Component({
    selector: 'auth-sign-in',
    templateUrl: './sign-in.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        ...MatModules,
        CommonModule,
        RouterLink,
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        StartupLogoComponent,
        CopyrightComponent,
    ],
})
export class AuthSignInComponent implements OnInit {
    @ViewChild('signInNgForm') signInNgForm!: NgForm;
    signInForm!: UntypedFormGroup;
    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    showAlert: boolean = false;
    redirectURL = '/';

    constructor(
        private _alertService: AlertService,
        private _firebaseService: FirebaseService,
        private _firebase2FAService: Firebase2FAService,
        private _activatedRoute: ActivatedRoute,
        private _authService: AuthService,
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        private route: ActivatedRoute
    ) {}

    // ----------------------------------------------------------------------
    // @ Lifecycle hooks
    // ----------------------------------------------------------------------

    ngOnInit(): void {
        this.redirectURL =
            this.route.snapshot.queryParamMap.get('redirectURL') || '/';

        // Create the form
        this.signInForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            rememberMe: [''],
        });
    }

    // ----------------------------------------------------------------------
    // @ Public methods
    // ----------------------------------------------------------------------

    signIn(): void {
        if (this.signInForm.invalid) return;
        this.signInForm.disable();
        this.showAlert = false;
        this._authService
            .signInAndSendToken(
                this.signInForm.value.email,
                this.signInForm.value.password
            )
            .subscribe({
                next: (user: User) => {
                    this._router.navigateByUrl('/verify-2fa', {
                        state: {
                            user: user,
                            redirectURL: this.redirectURL,
                        },
                    });
                },
                error: (error) => {
                    if (error.code === 'auth/multi-factor-auth-required') {
                        this._router.navigateByUrl('/verify-2fa', {
                            state: {
                                firebaseError: error,
                                redirectURL: this.redirectURL,
                            },
                        });
                        return;
                    }

                    // Re-enable the form
                    this.signInForm.enable();

                    // Reset the form
                    this.signInNgForm.resetForm();

                    // Show Alert
                    if (error.code !== 'auth/multi-factor-auth-required') {
                        this._alertService.showAlert(
                            this._firebaseService.getFirebaseErrorMessage(
                                error
                            ),
                            'error'
                        );
                    }
                },
            });
    }

    // ----------------------------------------------------------------------
    // @ Dev methods
    // ----------------------------------------------------------------------

    isPrivateLogin: boolean = false;
    firebaseTokenForm: FormGroup = this._formBuilder.group({
        token: [''],
    });

    /**
     * Private login only available for development purposes
     */
    privateLogin(): void {
        // // Next -> user/roles.service.ts 19.
        // this._rolesService.directAccess = true;
        // this._authService.signIn(this.signInForm.value).subscribe((res) => {
        //     const redirectURL =
        //         this._activatedRoute.snapshot.queryParamMap.get(
        //             'redirectURL'
        //         ) || '/dashboard';
        //     // Navigate to the redirection url. Dashboard
        //     this._router.navigateByUrl(redirectURL);
        // });
        this.isPrivateLogin = !this.isPrivateLogin;
    }

    enterFirebaseToken(): void {
        this._authService
            .forceLogin(this.firebaseTokenForm.get('token')?.value)
            .pipe(take(1))
            .subscribe((res) => {
                const redirectURL =
                    this._activatedRoute.snapshot.queryParamMap.get(
                        'redirectURL'
                    ) || '/dashboard';

                // Navigate to the redirection url. En este caso el dashboard
                try {
                    this._router.navigateByUrl(redirectURL);
                } catch (error) {
                    console.log('ERROR' + error.message);
                }
            });
    }
}
